/*
    This file is the base theming configuration file for the Sitbank cua instance.
    This file is part of the theming config and should only be updated inside the /theme/ branches in the repository.
    When you make changes to this file, please update the below string so we can always stay +1 to the base repo version of the file.

    Iteration: 1

*/

const thirdParty = {
  calculators: {
    test: {
      // we test against this selector to see if we need to load the third party script.
      // selector: 'iframe[src*="gbst.com"]',
      selector: 'iframe',
      // list the attributes we need to get from the selector to make it work.
      attributes: ['id'],
      // list any subelements that we need to get from the selected element.
      // note that in this instance because we are loading from an iframe we cannot access any DOM elements within, so anything here will return and empty NodeList.
      elements: [],
    },
    script: {
      // if we do need to run the script, here's the src and an ID so that we only load it once.
      src: 'https://calculators.gbst.com/clients/standard_suite/lib/iframeResizer.min.js',
      id: 'iframeResizerScript',
      // we add a callback function to be run once that loaded script's onload event fires.
      callback: null,
    },
    callback: {
      attributes: function ({ id }) {
        if (typeof iFrameResize === 'function') {
          iFrameResize({}, `#${id}`)
        }
      },
      elements: function (elements) {
        // any function that requires subelements of the selected parents elements goes in here.
      },
    },
  },

  d365Forms: {
    test: {
      selector: '[data-form-id]',
      attributes: ['data-form-id'],
      elements: [],
    },
    script: {
      // Loader URL is sourced from appsettings (D365Forms:FormLoaderScriptUrl) and injected
      // at runtime via window.pageData — see applyFormLoaderScriptUrl() in App.jsx. Left empty
      // here on purpose so the URL stays in server config rather than being hardcoded.
      src: '',
      id: 'd365FormLoaderScript',
      callback: null,
    },
    callback: {
      attributes: function () {
        // FormLoader.bundle.js auto-initialises all [data-form-id] elements on load
      },
      elements: function () {},
    },
  },

  // typekit: {
  //   test: {
  //     selector: 'body',
  //     attributes: [],
  //     elements: [],
  //   },
  //   script: {
  //     src: 'https://use.typekit.net/jdh1wwd.css',
  //     id: 'typekitScript',
  //     type: 'link',
  //     callback: null,
  //     location: 'head',
  //   },
  // },
}

/* if you are adding a local font for a client, you will also need to edit fe-src\src\css\base\_localFonts.css to add the font-family details */
/* Helvetica Neue is a system font — no external font loading is required. */
/* To switch to a web font, uncomment and configure one of the options below. */
const viteFontSetup = {
  // google: {
  //   families: [
  //     {
  //       name: 'Playfair Display',
  //       styles: 'ital,wght@0,400;0,700;0,900;1,400;1,700;1,900',
  //       defer: true,
  //     },
  //   ],
  // },
  // custom: {
  //   families: [
  //     {
  //       name: 'PPEditorialNew',
  //       local: 'PPEditorialNew',
  //       src: './public/fonts/PPEditorialNew-Regular.woff',
  //       transform(font) {
  //         if (font.basename === 'PPEditorialNew') {
  //           font.weight = 400
  //         }
  //         return font
  //       },
  //     },
  //   ],
  // },
}

const themeConfig = {
  thirdParty,
  hexBasedPalette: [
    // defining this palette of the base colours will allow us better interaction with the colours pickers from the CMS that only return the colour value.
    // one limitation of this is that only the '500' level colours will be supported in the picker, although the colour variable can be manipulated via the shade selectors in the templates.
    '000000',
    'ffffff',
    'e2e8f0',
    '475569',
    'ff0f85',
    'a60c7e',
    '4b054f',
  ],
  settings: {
    // this will close all other accordions in the same component when a new one is open
    onlyOneAccordionOpen: true,
    // this setting will set a more 'readable' width constraint to various components.
    // it's probably a good idea to turn it on, but the default will be off, so as not to break existing cient designs.
    useReadableContentWidth: false,
    // there are two types of button available, rounded rectangles and pill-shaped.
    // set this to true to lock all buttons to pill-shaped, or false to default to rounded-rectangles
    useOnlyPillButtons: false,
    // if a button colour selection matches the colour of a button in the component
    // true: switch to a white button
    // false: switch to a lighter shadew of the same theme colour.
    useWhiteButtonsOnCollision: true,
    showExternalLinkIcons: false,
    heroForegroundImages: true,
    // useTextShadow: ['images', 'always'] | false
    // images is for sections with background images
    // always is for... always
    // false is don't use shadows at all.
    useTextShadow: 'images',
    ratesCard: {
      highlightRate: true,
    },
    buttonShades: {
      standard: {
        base: '500',
        hover: '400',
      },
      muted: {
        base: '300',
        hover: '400',
      },
    },
    backToTop: 'radiance',
    searchPageStub: 'search',
    useCMSMenuAccents: true,
    hero: {
      card: {
        shadow: {
          base: 'inherit',
          // it is unlikely that you wil want this to be the full colour, as it will be quite stark, so it's recommended that you specifically add the class `shadow-[color-value]/50` to the safelist below to ensure that the colour choice is loaded (unless you use 'inherit' of course).
          hover: 'inherit',
        },
      },
    },
    nav: {
      desktop: {
        base: 'balance-500',
        active: 'balance-500',
        highlight: 'radiance',
      },
      mobile: {
        base: 'balance-500',
        active: 'balance-700',
      },
      footer: {
        base: 'balance-500',
        disclaimers: 'slate-400',
        inheritLinkStyle: false,
      },
      sticky: {
        base: 'midnight-300',
      },
      breadcrumbs: {
        base: 'breadcrumbs-light',
      },
      search: {
        base: 'balance-500',
      },
    },
    alerts: {
      success: {
        bg: 'green-50',
        text: 'green-800',
      },
      primary: {
        bg: 'slate-200',
        text: 'slate-200-text',
      },
      danger: {
        bg: 'og-red-700',
        text: 'og-red-700-text',
      },
      secondary: {
        bg: 'og-red-100',
        text: 'og-red-800',
      },
      warning: {
        bg: 'optimism-100',
        text: 'optimism-700',
      },
      light: {
        bg: 'slate-50',
        text: 'gray-500',
      },
      dark: {
        bg: 'og-red-900',
        text: 'balance-300',
      },
    },
  },
  // this flag will tell the system to generate text-colors for each base colour that is present.
  detectInverseText: true,
  // these colours can be presented in either HEX or RBG notation, either will work for the inverse text detection
  colors: {
    text: {
      // the color for dark text on a light background, not the color that goes over dark
      light: 'midnight-500',
      // the color for light text on a dark background, not the color that goes over light
      dark: 'balance-300',
    },
    link: {
      default: {
        // the color for dark text on a light background, not the color that goes over dark
        light: 'radiance-400',
        // the color for light text on a dark background, not the color that goes over light
        dark: 'radiance-300',
      },
      hover: {
        // the color for dark text on a light background, not the color that goes over dark
        light: 'balance-600',
        // the color for light text on a dark background, not the color that goes over light
        dark: 'midnight-400',
      },
    },
  },
  font: {
    vite: viteFontSetup,
  },
}

export default themeConfig
